import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Description, Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { appDialogStore } from '../../services/appDialogs';
import './DialogSurface.css';

function DialogRequest({ dialog }) {
  const { id, kind, message, defaultValue, options } = dialog;
  const [value, setValue] = useState(defaultValue);
  const cancelRef = useRef(null);
  const inputRef = useRef(null);
  const acknowledgeRef = useRef(null);
  const isPrompt = kind === 'prompt';
  const isAlert = kind === 'alert';
  const close = () => appDialogStore.cancel(id);
  const title = options.title || (isPrompt ? '输入内容' : isAlert ? '提示' : '请确认');

  return (
    <Dialog
      open
      onClose={close}
      initialFocus={isPrompt ? inputRef : isAlert ? acknowledgeRef : cancelRef}
      className="app-dialog relative z-[10000]"
    >
      <DialogBackdrop className="app-dialog__backdrop fixed inset-0" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          className="app-dialog__panel relative w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto p-6"
          onKeyDown={(event) => {
            // Text entry / Space / editor shortcuts belong to this modal only.
            event.stopPropagation();
            if (event.key === 'Escape' && !event.defaultPrevented && !event.nativeEvent.isComposing) {
              event.preventDefault();
              close();
            }
          }}
          onKeyUp={(event) => event.stopPropagation()}
        >
          <div className="flex items-center gap-3 pr-8">
            <span className="app-dialog__icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 10.5v6M12 7.5h.01" />
              </svg>
            </span>
            <DialogTitle className="app-dialog__title">{title}</DialogTitle>
          </div>
          <button type="button" className="app-dialog__close absolute right-4 top-4" onClick={close} aria-label="关闭弹窗">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
          <Description className="app-dialog__message mt-4 whitespace-pre-line break-words">{message}</Description>
          <form onSubmit={(event) => { event.preventDefault(); appDialogStore.accept(id, value); }}>
            {isPrompt ? (
              <input
                ref={inputRef}
                className="app-dialog__input mt-4 w-full"
                type={options.inputType === 'password' ? 'password' : 'text'}
                value={value}
                onChange={(event) => setValue(event.target.value)}
                aria-label={options.inputLabel || title}
                placeholder={options.placeholder || ''}
                autoComplete={options.inputType === 'password' ? 'new-password' : 'off'}
              />
            ) : null}
            <div className="app-dialog__footer mt-6 flex justify-end gap-2">
              {!isAlert ? (
                <button type="button" ref={cancelRef} className="app-dialog__button" onClick={close}>
                  {options.cancelLabel || '取消'}
                </button>
              ) : null}
              <button
                type="submit"
                ref={acknowledgeRef}
                className="app-dialog__button"
                data-intent={options.variant === 'danger' ? 'danger' : 'primary'}
              >
                {options.confirmLabel || (isAlert ? '知道了' : '确认')}
              </button>
            </div>
          </form>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export default function AppDialogHost() {
  const dialog = useSyncExternalStore(appDialogStore.subscribe, appDialogStore.getSnapshot, appDialogStore.getServerSnapshot);
  useEffect(() => appDialogStore.mountHost(), []);
  return dialog ? <DialogRequest key={dialog.id} dialog={dialog} /> : null;
}
